'use client';

import {
  isValidElement,
  ReactElement,
  useCallback, useEffect, useState,
} from 'react';
import {
  CvDetailExperiencePlaceholder,
  CvDetailProfileHeadingPlaceholder,
} from '@components/placeholders/cv';
import { ProfileResumeSections } from '@components/templates/cv/sections';
import {
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
  CvDetailRegisteredActions,
} from '@components/templates/cv/interface/CvDetailInterface';
import { BlurredCvDetail } from '@components/templates/cv/blurred/BlurredCvDetail';
import { ProfileSimilarWrapper } from '@components/templates/cv/sections/ProfileSimilarWrapper';
import { useCvDetails } from '@components/templates/cv/hooks';

// Redux
import { useAppSelector } from 'src/app';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSearchParams } from 'next/navigation';
import { useTwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout/TwoColumnLayoutContext';
import { MAIN_OFFSET_OPEN_CLASS, MAIN_OFFSET_COLLAPSED_CLASS } from '@components/layout/Layouts/TwoColumnLayout';
import { TopAnchorLinks } from '@components/templates/cv/sections/TopAnchorLinks';
import { BaseTrackingEventData, trackEvent } from '@services/tracking';
import { UnblockActions } from '@components/templates/cv/sections/operations/UnblockActions';
import { Button } from '@components/common';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useSwrGetSearchPackAggregatedDetails } from '@hooks/useSwrApi/endpoints/user-searchpack/useSwrGetSearchPackAggregatedDetails';

// Interface
// ************************************
export interface CvDetailHubProps {
  slug?: string,
  jobSlug?: string,
  isPassiveRecommendation?: boolean,
  isAuthenticated?: boolean,
  isSinglePage?: boolean,
  closeModal?: () => void,
  statusChangeFromModal?:(statusId: string, slug:string)=>void;
  queryParams?: string;
  withNavbarInModal?: boolean;
  closeControl?: ReactElement | undefined
  trackingData? : BaseTrackingEventData;
  swrTalentList?: { items?: ApiSchema<'ResumeCardData'>[]; total?: number }[];
  openTalentModal?: (slug: string, as?: string) => void;
  showLoadMore?: boolean;
  size?: number;
  setSize?: (size: number) => void;
}

/**
 * @description CV Detail Sections Hub. Used for rendering a CV regardless of context (job, search, own).
 * - **slug** - string - The CV slug, optional.
 * - **jobSlug** - string - The job slug, optional.
 * - **isAuthenticated** - boolean - If the user is authenticated, optional.
 * - **closeModal** - function - Function to close the modal (if viewing in a modal), optional.
 * - **isSinglePage** - boolean- If the user is on the single page instead of modal
 * - **queryParams** - string - The querystring taken from the current URL, optional.
 * - **withNavbarInModal** - boolean - If the header navbar is included in modal, optional.
 */
export const CvDetailHub = (props: CvDetailHubProps) => {
  // Destructure props
  const {
    slug,
    jobSlug,
    isPassiveRecommendation,
    isSinglePage,
    isAuthenticated,
    closeModal,
    statusChangeFromModal,
    queryParams,
    withNavbarInModal,
    closeControl,
    trackingData,
    swrTalentList,
    openTalentModal,
    showLoadMore,
    size,
    setSize,
  } = props;


  // Get locale from router
  // ************************************
  const { locale } = useExtendedRouter();

  // Get the url search params and get the rid
  // ************************************
  const searchParams = useSearchParams();
  const rid = searchParams?.get('rid') || '';


  // Get header info data for finding out the user's role
  // ************************************
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userRole = headerInfoData?.role;
  const userId = headerInfoData?.userId;

  // Whether the CV detail is being viewed by a COMPANY.
  // Optimistic rendering: assume company user if jobSlug exists (job context pages are protected by authentication)
  const isCompany = !!headerInfoData || !!jobSlug;

  // Fetch aggregated search pack data to check if employer has sourcing package activated
  // This is fetched once here and passed to child components to avoid multiple API calls
  const effectiveJobSlug = jobSlug || '';
  const { data: aggregatedSearchPackData } = useSwrGetSearchPackAggregatedDetails({
    locale,
    jobSlug: effectiveJobSlug,
    shouldFetch: isCompany && !!effectiveJobSlug,
  });
  const hasSourcingPackage = (aggregatedSearchPackData?.availableUnlocks ?? 0) > 0;

  // const headerInfo = useAppSelector(getUserHeaderInfo);
  // Get CV details
  // ************************************
  const {
    cvDetail,
    showBlurredCv,
    isCvLoaded,
    setCvLoaded,
    phoneHighlight,
    setPhoneHighlight,
    availableResumeInformations,
    applicantSeenContactStatus,
    setJobFolderInfo,
    setApplicantSeenContactStatus,
    fetchResumeDetail,
    jobFolderInfo,
  } = useCvDetails({
    rid,
    slug,
    isCompany,
    toFetchOperationJob: !jobSlug,
    locale,
    ...(jobSlug && { jobSlug }),
    ...(isPassiveRecommendation && { isPassiveRecommendation }),
  });

  // States
  // ************************************
  const [currentCVStatus, setCurrentCVStatus] = useState<CvDetailAvailableOperationsCurrentStatus>();
  const [jobFolderHighlight, setJobFolderHighlight] = useState<boolean>(false);
  // eslint-disable-next-line no-spaced-func
  const [downloadCvHandler, setDownloadCvHandler] = useState<{ fn: () => void; }>();
  const [cvActions, setCvActions] = useState<CvDetailRegisteredActions>();


  // Helper functions
  // ************************************
  const getCurrentCvStatus = useCallback((item:CvDetailAvailableOperationsCurrentStatus) => setCurrentCVStatus(item), []);
  const applicantSeenContact = () => setApplicantSeenContactStatus(true);
  const setTelephoneHighlight = () => setPhoneHighlight(true);
  const setCurrentJobFolderHighlight = () => setJobFolderHighlight(true);
  const setLoadingCvStatus = () => setCvLoaded(false);

  // Stable register callback with bail-out: if `UnblockActions` calls this on
  // every render with the same fn reference, we skip the state update to avoid
  // an infinite render loop.
  const handleRegisterDownloadCv = useCallback((fn: () => void) => {
    setDownloadCvHandler((prev) => (prev?.fn === fn ? prev : { fn }));
  }, []);

  // Same pattern as handleRegisterDownloadCv: bail out when the actions object
  // reference hasn't changed. UnblockActions must memoize the actions it
  // passes (via useMemo) so that this setter only creates a new state when
  // something meaningful changes.
  const handleRegisterActions = useCallback((actions: CvDetailRegisteredActions) => {
    setCvActions((prev) => (prev === actions ? prev : actions));
  }, []);


  useEffect(() => {
    const timer = setTimeout(() => {
      if (slug) {
        trackEvent({
          eventType: 'view',
          entityType: 'resume',
          entityId: slug ?? '',
          viewContext: 'detail',
          ...(userId && { userId: userId.toString() }),
          ...(rid && { listUuid: rid }),
          ...(trackingData?.listType && { listType: trackingData?.listType }),
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [slug, userId, rid, trackingData]);

  const twoColumnLayout = useTwoColumnLayout();

  // Don't render if there is no slug
  // ************************************
  if (!slug) return null;

  // Render component
  // ************************************
  // Offset the CV content by the MC sidebar width only when rendered inside a
  // full-screen modal on top of TwoColumnLayout (the modal escapes the layout's
  // <main>, which would otherwise apply the offset). On a standalone page
  // (`isSinglePage`), the containing <main> already provides the offset, so
  // adding it here would double-shift the content.
  let sectionMargin = '';
  if (twoColumnLayout && !isSinglePage) {
    sectionMargin = twoColumnLayout.isLeftOpen ? ` ${MAIN_OFFSET_OPEN_CLASS}` : ` ${MAIN_OFFSET_COLLAPSED_CLASS}`;
  }

  return (
    <section className={`grow pb-24 transition-all duration-300${sectionMargin}`}>


      {isCvLoaded && !showBlurredCv && (
        <UnblockActions
          applicantSeenContactStatus={applicantSeenContactStatus}
          applicantSeenContact={applicantSeenContact}
          setLoadingCvStatus={setLoadingCvStatus}
          setJobFolderInfo={setJobFolderInfo}
          setCurrentJobFolderHighlight={setCurrentJobFolderHighlight}
          setTelephoneHighlight={setTelephoneHighlight}
          isFromJobContext={!!jobSlug}
          jobFolderId={jobFolderInfo?.jobId ?? 0}
          jobSlug={jobSlug || jobFolderInfo?.jobSlug}
          jobTitle={jobFolderInfo?.title ?? ''}
          reFetchData={fetchResumeDetail}
          sendCurrentCvStatus={getCurrentCvStatus}
          cvDetail={cvDetail as CvDetailObject}
          slug={slug}
          closeCvModal={closeModal}
          statusChangeFromModal={statusChangeFromModal}
          isCompany={isCompany}
          rid={rid}
          withNavbarInModal={withNavbarInModal}
          isSinglePage={isSinglePage}
          locale={locale}
          hasSourcingPackage={hasSourcingPackage}
          onRegisterDownloadCv={handleRegisterDownloadCv}
          onRegisterActions={handleRegisterActions}
          swrTalentList={swrTalentList}
          openTalentModal={openTalentModal}
          showLoadMore={showLoadMore}
          size={size}
          setSize={setSize}
        />
      )}

      { isValidElement(closeControl) && closeControl }

      <div className="pb-20">
        { !isCvLoaded && (
          <>
            <div className="border-b bg-surface lg:block">
              <div className="container flex items-center justify-between py-2">
                <div className="flex items-center">
                  { /* Close modal button */
                    closeModal && (
                      <Button styling="none" onClick={closeModal}>
                        <span className="mr-3 flex size-7 items-center justify-center rounded-full bg-surface md:size-8">
                          <XMarkIcon className="size-5 text-ink" />
                        </span>
                      </Button>
                    )
                  }
                  <div className="mx-auto mr-4 size-6 rounded-full bg-slate-200 md:size-8" />
                </div>

                <div className="flex items-center">
                  <div className="mx-auto ml-4 size-6 rounded-full bg-slate-200 md:size-8" />
                  <div className="mx-auto ml-4 mr-2 size-6 rounded-full bg-slate-200 md:mr-0 md:size-8" />

                </div>
              </div>

              <div className="container mx-auto ml-6 grid min-h-dvh grid-cols-7 gap-5 px-0 py-5 lg:grid lg:px-4">
                <div className="col-span-4 hidden lg:block">
                  <CvDetailExperiencePlaceholder />
                  <CvDetailExperiencePlaceholder />
                  <CvDetailExperiencePlaceholder />
                </div>
                <div className="col-span-3 hidden border-l bg-surface px-4 lg:block">
                  <CvDetailProfileHeadingPlaceholder />
                  <CvDetailExperiencePlaceholder />
                  <CvDetailExperiencePlaceholder />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex w-full">
          <div className="w-full">
            <div className="bg-surface pb-10">
              {isCvLoaded
                && <TopAnchorLinks cvDetail={cvDetail} />}

              { /* Show the profile sections if data available and user has access */
                isCvLoaded && !showBlurredCv && (
                  <div className="mx-auto px-4 lg:pl-8 lg:pr-16">
                    <ProfileResumeSections
                      cvDetail={cvDetail as CvDetailObject}
                      applicantSeenContactStatus={applicantSeenContactStatus}
                      cvCurrentJobUnlockFolder={jobFolderInfo}
                      jobFolderInfo={jobFolderInfo}
                      phoneHighlight={phoneHighlight}
                      jobFolderHighlight={jobFolderHighlight}
                      currentCVStatus={currentCVStatus as CvDetailAvailableOperationsCurrentStatus}
                      {...(jobSlug && { jobFolderSlug: jobSlug, jobSlug })}
                      queryParams={queryParams}
                      isCompany={isCompany}
                      locale={locale}
                      revalidateCv={fetchResumeDetail}
                      onDownloadCv={downloadCvHandler?.fn}
                      cvActions={cvActions}
                    />
                  </div>
                )
              }
            </div>

            <div className="bg-surface-100 px-4 py-10">
              { /* Show the profile sections if data available and user has access */
                isCvLoaded && !showBlurredCv && (
                  <div className="mx-auto">
                    <ProfileSimilarWrapper
                      cvDetail={cvDetail as CvDetailObject}
                      jobSlug={jobSlug || jobFolderInfo?.jobSlug}
                      locale={locale}
                    />
                  </div>
                )
              }
            </div>
          </div>
        </div>


      </div>

      { /* Show the blurred CV sections if data available and no access */
        isCvLoaded && showBlurredCv && (
          <BlurredCvDetail
            setLoadingCvStatus={setLoadingCvStatus}
            isFromJobContext={!!jobSlug}
            slug={slug}
            jobSlug={jobSlug || jobFolderInfo?.jobSlug}
            availableResumeInformations={availableResumeInformations as CvDetailObject}
            setUnlockFromBlurred={fetchResumeDetail}
            closeCvModal={closeModal}
            isAuthenticated={isAuthenticated}
            userRole={userRole}
            locale={locale}
          />
        )
      }
    </section>
  );
};
